import { store } from './store.js'
import { rememberReturnTo } from './return-to.js'

let accessToken = null

export function setAccessToken(token) {
  accessToken = token
}

export function clearAccessToken() {
  accessToken = null
}

export function getAccessToken() {
  return accessToken
}

// Data APIs live under /api; auth and OAuth endpoints stay at the root (§20.5 —
// a third-party client posts to /oauth/token, so that prefix cannot move).
function resolve(path) {
  return path.startsWith('/auth') || path.startsWith('/oauth') ? path : `/api${path}`
}

async function doFetch(method, path, body, headers) {
  return fetch(resolve(path), {
    method,
    headers,
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined
  })
}

async function request(method, path, body) {
  const headers = { 'X-Requested-With': 'XMLHttpRequest' }
  // Only declare a JSON body when one is actually sent — Fastify rejects an empty
  // body when Content-Type is application/json (e.g. bodyless POSTs).
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`

  let res = await doFetch(method, path, body, headers)

  if (res.status === 401 && accessToken) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`
      res = await doFetch(method, path, body, headers)
    } else {
      clearAccessToken()
      store.clear()
      // A session that expired mid-visit gets the same treatment as one that was
      // never there: remember the page, then send them to sign in.
      rememberReturnTo(window.location.pathname + window.location.search)
      window.location.href = '/login'
      return
    }
  }

  return handleResponse(res)
}

async function handleResponse(res) {
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw Object.assign(new Error(data?.message || res.statusText), {
      statusCode: res.status,
      code: data?.code
    })
  }
  return data
}

// Single-flight, and it has to be. Three callers refresh independently — request(),
// upload(), and the SSE reconnect in live.js — and they routinely fire together: one
// expired access token 401s every request a page mounted, and the event stream retries
// on its own timer. Left unguarded, each sends its own rotation of the SAME cookie, the
// first wins, and the losers are handed a token that no longer exists. That is a
// working session being thrown away mid-navigation, not an expired one.
//
// Sharing one in-flight promise means concurrent callers rotate once and all wake with
// the same new access token. (The server also grants a short grace to a just-rotated
// token, §4.5 — this stops the stampede, that survives the one that slips through.)
let refreshInFlight = null

export function tryRefresh() {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    try {
      // No body is sent (the refresh token comes from the HttpOnly cookie), so we
      // must NOT set Content-Type: application/json — Fastify rejects an empty body
      // with that content-type.
      const res = await fetch('/auth/token/refresh', {
        method: 'POST',
        credentials: 'include'
      })
      if (!res.ok) return false
      const data = await res.json()
      setAccessToken(data.accessToken)
      return true
    } catch {
      return false
    }
  })().finally(() => { refreshInFlight = null })
  return refreshInFlight
}

// Multipart upload. Sends FormData (no Content-Type header — the browser sets the
// multipart boundary) with the same auth + 401-refresh-retry behavior as request().
async function upload(path, file, query = '') {
  const build = () => {
    const form = new FormData()
    form.append('file', file)
    const headers = { 'X-Requested-With': 'XMLHttpRequest' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    return fetch(resolve(path) + query, { method: 'POST', headers, credentials: 'include', body: form })
  }

  let res = await build()
  if (res.status === 401 && accessToken) {
    if (await tryRefresh()) res = await build()
    else {
      clearAccessToken()
      store.clear()
      rememberReturnTo(window.location.pathname + window.location.search)
      window.location.href = '/login'
      return
    }
  }
  return handleResponse(res)
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  put: (path, body) => request('PUT', path, body),
  delete: (path) => request('DELETE', path),
  upload
}
